import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import React from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext, AuthData } from '../../context/auth';
import { ClaimContext, ClaimData } from '../../context/claim';
import DropdownButton from '../DropdownButton';
import { errorToast, successToast } from '../Toast';
import AddPayableModal from './AddPayableModal';
import UpdateReservesModal from './UpdateReservesModal';

interface Props {
	authData: AuthData;
	claimData: ClaimData;
}

const SubclaimActions = (props: Props) => {
	const { claimData, authData } = props;
	const hasPermissions = authData.hasPermission;
	const { claim, setClaimUpdateReq, setLoading } = claimData;
	const params = useParams();
	const { subclaimId } = params;

	const subclaim = claim.subClaims.find((sC) => sC.locator === subclaimId);

	if (!subclaim) {
		return null;
	}

	const closeSubclaim = async () => {
		setLoading(true);
		const payload = {
			updateSubClaims: [
				{
					locator: subclaimId,
					status: 'closed',
				},
			],
		};
		try {
			await claimData.updateClaim(claim.locator, payload);
			successToast('Successfully closed subclaim');
			setClaimUpdateReq({});
		} catch (error) {
			errorToast('Failed to close subclaim');
		}
		setLoading(false);
	};

	const reopenSubclaim = async () => {
		setLoading(true);
		const payload = {
			updateSubClaims: [
				{
					locator: subclaimId,
					status: 'open',
				},
			],
		};
		try {
			await claimData.updateClaim(claim.locator, payload);
			successToast('Successfully reopened subclaim');
			setClaimUpdateReq({});
		} catch (error) {
			errorToast('Failed to reopen subclaim');
		}
		setLoading(false);
	};

	const hiddenOptions = [];

	if (subclaim.currentStatus === 'open') {
		hasPermissions('claim.discard') &&
			hiddenOptions.push({ label: 'Close Subclaim', callback: closeSubclaim });
		return (
			<Stack direction='row' spacing={1}>
				{hasPermissions('claim.createPayable') && (
					<AddPayableModal subclaim={subclaim} />
				)}
				{hasPermissions('claim.update') && (
					<UpdateReservesModal subclaim={subclaim} />
				)}
				{hiddenOptions.length > 0 && <DropdownButton actions={hiddenOptions} />}
			</Stack>
		);
	} else {
		if (claim.currentStatus === 'closed') {
			return null;
		}
		// closed
		return (
			<Stack direction='row' spacing={1}>
				<Button color='primary' onClick={reopenSubclaim} variant='contained'>
					Reopen Subclaim
				</Button>
			</Stack>
		);
	}
};

function withContext<T>(Component: any) {
	return (props: Omit<T, 'authData' | 'claimData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ClaimContext.Consumer>
					{(claimData) => (
						<Component {...props} claimData={claimData} authData={authData} />
					)}
				</ClaimContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext<Props>(SubclaimActions);
